import React, { useState } from 'react';
import styles from '../../../styles/LoginRegister.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import type { AgencyRegistrationDetails } from '../../../types/Registration';

interface AgencyDetailsStepProps {
  data: AgencyRegistrationDetails;
  onUpdate: (data: AgencyRegistrationDetails) => void;
  onNext: () => void;
  onBack: () => void;
}

const AgencyDetailsStep: React.FC<AgencyDetailsStepProps> = ({ data, onUpdate, onNext, onBack }) => {
  const [errors, setErrors] = useState<Partial<AgencyRegistrationDetails>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply mask for company registration number
    if (name === 'companyRegistrationNumber') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      
      // Apply the mask: xxxx/xxxxxx/xx
      if (digitsOnly.length <= 4) {
        processedValue = digitsOnly;
      } else if (digitsOnly.length <= 10) {
        processedValue = `${digitsOnly.slice(0, 4)}/${digitsOnly.slice(4)}`;
      } else {
        processedValue = `${digitsOnly.slice(0, 4)}/${digitsOnly.slice(4, 10)}/${digitsOnly.slice(10, 12)}`;
      }
    }

    onUpdate({ ...data, [name]: processedValue });
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof AgencyRegistrationDetails]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateStep = (): boolean => {
    const newErrors: Partial<AgencyRegistrationDetails> = {};

    if (!data.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!data.tradingAsName.trim()) {
      newErrors.tradingAsName = 'Trading-as name is required';
    }

    if (!data.companyRegistrationNumber.trim()) {
      newErrors.companyRegistrationNumber = 'Company registration number is required';
    } else if (!/^\d{4}\/\d{6}\/\d{2}$/.test(data.companyRegistrationNumber)) {
      newErrors.companyRegistrationNumber = 'Please enter a valid registration number (format: xxxx/xxxxxx/xx)';
    }

    if (!data.streetNumber.trim()) {
      newErrors.streetNumber = 'Street number is required';
    }
    if (!data.streetName.trim()) {
      newErrors.streetName = 'Street name is required';
    }
    if (!data.suburbName.trim()) {
      newErrors.suburbName = 'Suburb is required';
    }
    if (!data.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!data.postalCode.trim()) {
      newErrors.postalCode = 'Postal code is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      onNext();
    }
  };

  return (
    <div className={styles.formSection}>
      <h2 className={styles.formTitle}>Agency Details</h2>
      <p className={styles.formDescription}>
        Please provide your agency information to set up your business account.
      </p>
      
      <form className={styles.form} onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
        <div className={styles.formSectionSpacing}>
          <h3 className={styles.sectionHeader}>Company Information</h3>
          
          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Company Name *</label>
            <input
              type="text"
              name="companyName"
              value={data.companyName}
              onChange={handleChange}
              placeholder="Enter your company name"
              className={styles.fieldInput}
              required
            />
            {errors.companyName && <div className={styles.errorMessage}>{errors.companyName}</div>}
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Trading-As Company Name *</label>
            <input
              type="text"
              name="tradingAsName"
              value={data.tradingAsName}
              onChange={handleChange}
              placeholder="Enter your trading-as name"
              className={styles.fieldInput}
              required
            />
            {errors.tradingAsName && <div className={styles.errorMessage}>{errors.tradingAsName}</div>}
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Company Registration Number *</label>
            <input
              type="text"
              name="companyRegistrationNumber"
              value={data.companyRegistrationNumber}
              onChange={handleChange}
              placeholder="xxxx/xxxxxx/xx"
              className={styles.fieldInput}
              maxLength={14}
              required
            />
            {errors.companyRegistrationNumber && <div className={styles.errorMessage}>{errors.companyRegistrationNumber}</div>}
          </div>

        </div>

        <div className={styles.formSectionSpacing}>
          <h3 className={styles.sectionHeader}>Company Physical Address</h3>

          <div className={styles.fieldRow}>
            <div className={styles.fieldContainer}>
              <label className={styles.fieldLabel}>Unit Number</label>
              <input
                type="text"
                name="unitNumber"
                value={data.unitNumber ?? ''}
                onChange={handleChange}
                placeholder="e.g. 4"
                className={styles.fieldInput}
              />
            </div>

            <div className={styles.fieldContainer}>
              <label className={styles.fieldLabel}>Complex / Building Name</label>
              <input
                type="text"
                name="complexName"
                value={data.complexName ?? ''}
                onChange={handleChange}
                placeholder="e.g. Sandton Towers"
                className={styles.fieldInput}
              />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.fieldContainer}>
              <label className={styles.fieldLabel}>Street Number *</label>
              <input
                type="text"
                name="streetNumber"
                value={data.streetNumber}
                onChange={handleChange}
                placeholder="e.g. 12"
                className={styles.fieldInput}
                required
              />
              {errors.streetNumber && <div className={styles.errorMessage}>{errors.streetNumber}</div>}
            </div>

            <div className={styles.fieldContainer}>
              <label className={styles.fieldLabel}>Street Name *</label>
              <input
                type="text"
                name="streetName"
                value={data.streetName}
                onChange={handleChange}
                placeholder="e.g. Main Road"
                className={styles.fieldInput}
                required
              />
              {errors.streetName && <div className={styles.errorMessage}>{errors.streetName}</div>}
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.fieldContainer}>
              <label className={styles.fieldLabel}>Suburb *</label>
              <input
                type="text"
                name="suburbName"
                value={data.suburbName}
                onChange={handleChange}
                placeholder="e.g. Sandton"
                className={styles.fieldInput}
                required
              />
              {errors.suburbName && <div className={styles.errorMessage}>{errors.suburbName}</div>}
            </div>

            <div className={styles.fieldContainer}>
              <label className={styles.fieldLabel}>City *</label>
              <input
                type="text"
                name="city"
                value={data.city}
                onChange={handleChange}
                placeholder="e.g. Johannesburg"
                className={styles.fieldInput}
                required
              />
              {errors.city && <div className={styles.errorMessage}>{errors.city}</div>}
            </div>
          </div>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Postal Code *</label>
            <input
              type="text"
              name="postalCode"
              value={data.postalCode}
              onChange={handleChange}
              placeholder="e.g. 2196"
              className={styles.fieldInput}
              required
            />
            {errors.postalCode && <div className={styles.errorMessage}>{errors.postalCode}</div>}
          </div>
        </div>

        <div className={styles.buttonGroupSpaced}>
          <button type="button" onClick={onBack} className={`${buttonStyles.button} ${buttonStyles.formSecondary}`}>
            Back
          </button>
          <button type="submit" className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}>
            Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default AgencyDetailsStep;
